/**
 * 一个完全基于 text-overlap 的类，只有机器审核在用。相对于原来的 text-overlap 抛弃了一些业务上的接口
 * 之后可以基于 PointInPolygon 重写算法
 */

/* eslint-disable @tencent/i18next/no-literal-string */
import {
    IElementNode,
    ISlideNode,
    ITextElementBoxNode,
} from '@src/slide/application/service/slide-rating-and-improve/slide-content-analyzer.interface';
import { Quadtree } from '@src/slide/infrastructure/module/independent/quadtree/quadtree';
import { Rectangle } from '@src/slide/infrastructure/module/independent/quadtree/rectangle';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import {
    getOffsetOfTextBox,
    isStringVisible,
} from '@src/slide/application/service/slide-rating-and-improve/text/calculate/text-box-calculate';
import { twipsToPx, ITextBox, IRect } from '@tencent/alloy-editor';
import { getFrameAfterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import { checkRectIntersection } from '@src/slide/application/service/slide-rating-and-improve/text/calculate/quadtree-calculate';
import { FillType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';

type IOnOverlapDetected = (
    textNodeRect: Rectangle<ITextElementBoxNode>,
    backNodeRect: Rectangle<ITextElementBoxNode>,
    sortedRects: Rectangle<ITextElementBoxNode>[],
) => void;

export class TextOverlapBaseAnalyzer {
    private textQuadtree: Quadtree<Rectangle<ITextElementBoxNode>> | undefined;
    private currentSlideIndex: number;
    private presentation: IPresentationModel;


    public constructor() {
        this.reset();
    }

    public onOverlapDetected: IOnOverlapDetected = () => (void 0);

    public resetWithPresentation(presentation: IPresentationModel) {
        this.reset();
        this.presentation = presentation;
        this.textQuadtree = new Quadtree({
            width: this.presentation.props.slideWidth,
            height: this.presentation.props.slideHeight,
        });
    }

    public onSlideEnd(slideNode: ISlideNode) {
        this.textQuadtree?.clear();
    }

    public reset() {
        this.textQuadtree?.clear();
    }

    /**
     * 如果形状把文字遮住了，被遮住的文字就不再进行重叠检测
     * 此方案有局限性，因为形状不一定完全遮住 textBox
     */
    public checkNewElement(elementNode: IElementNode, detectShape = false) {
        const elementModel = elementNode.element;
        const frame = elementModel.getFrame().frameOnSlide();
        let bounds = frame;
        let hasRotate = false;
        if (frame.rotate !== undefined && frame.rotate !== 0) {
            hasRotate = true;
            bounds = getFrameAfterRotate(frame, frame.rotate);
        }
        const elementRect = new Rectangle({
            x: bounds.left,
            y: bounds.top,
            width: bounds.width,
            height: bounds.height,
            data: elementNode,
        });
        if (elementModel.type === UIModelType.SHAPE) {
            const fillType = elementModel.getBackgroundFill().getShownFillType();
            if (fillType === undefined || fillType === FillType.NoFill) {
                // 无填充不用处理
                return;
            }
            this.checkToDeleteTextRects(elementRect, detectShape);
        } else if ([UIModelType.IMAGE, UIModelType.TABLE, UIModelType.CHART].includes(elementModel.type)) {
            this.checkToDeleteTextRects(elementRect, detectShape);
        }
    }

    /**
     * 检测无填充的文本之间是否有重叠，会造成看不清
     * @param textElementBoxNode
     */
    public checkNewTextElementBoxNode(textElementBoxNode: ITextElementBoxNode) {
        if (textElementBoxNode.slideNode.slideIndex !== this.currentSlideIndex) {
            this.currentSlideIndex = textElementBoxNode.slideNode.slideIndex;
            this.textQuadtree?.clear();
        }
        if (this.checkNeedFilter(textElementBoxNode)) return;

        const actualRect = this.getActualRectFromTextBox(textElementBoxNode.textBox);
        const offset = getOffsetOfTextBox(textElementBoxNode.textBox);
        const elementModel = textElementBoxNode.element;
        const elementFrame = elementModel.getFrame().frameOnSlide();
        if (elementFrame.rotate !== undefined && elementFrame.rotate !== 0) {
            // 旋转比较复杂，先不支持处理
            // const center = {
            //     x: elementFrame.left + (elementFrame.width / 2),
            //     y: elementFrame.top + (elementFrame.height / 2),
            // };
            // boxBounds = rotateRectWithPoint(boxBounds, center, elementFrame.rotate);
            return;
        }
        const boxBounds = {
            left: twipsToPx(offset.x) + actualRect.x + elementFrame.left,
            top: twipsToPx(offset.y) + actualRect.y + elementFrame.top,
            width: actualRect.width,
            height: actualRect.height,
        };
        const textBound = new Rectangle({
            x: boxBounds.left,
            y: boxBounds.top,
            width: Math.floor(boxBounds.width),
            height: Math.floor(boxBounds.height),
        });
        const rects = this.textQuadtree?.retrieve(textBound) as Array<Rectangle<ITextElementBoxNode>>;
        const textBoxRect = new Rectangle<ITextElementBoxNode>({
            ...textBound,
            data: textElementBoxNode,
        });
        this.checkTextAndBackRects(textBoxRect, rects);
        this.textQuadtree?.insert(textBoxRect);
    }

    private checkTextAndBackRects(
        textNodeRect: Rectangle<ITextElementBoxNode>,
        rects: Array<Rectangle<ITextElementBoxNode>>,
    ): boolean {
        const sortedRects = rects.sort((a, b) => {
            const pathA = (a.data as ITextElementBoxNode).path;
            const pathB = (b.data as ITextElementBoxNode).path;
            const pathBLength = pathB.length;
            for (let i = 0; i < pathA.length; i++) {
                if (pathBLength < i) {
                    return 1;
                }
                if (pathA[i] !== pathB[i]) {
                    return pathB[i] - pathA[i];
                }
            }
            // 元素路径比较相同，进一步比较text路径
            const textPathA = (a.data as ITextElementBoxNode).textBoxPath;
            const textPathB = (b.data as ITextElementBoxNode).textBoxPath;
            const textPathBLength = textPathB.length;
            for (let j = 0; j < textPathA.length; j++) {
                if (textPathBLength < j) {
                    return 1;
                }
                if (textPathA[j] !== textPathB[j]) {
                    return textPathB[j] - textPathA[j];
                }
            }
            return 0;
        });
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < sortedRects.length; i++) {
            // 判断是否重叠，同一个文本框内的暂时先忽略
            const textNodeElement = (textNodeRect.data as ITextElementBoxNode).element;
            const backNodeElement = (sortedRects[i].data as ITextElementBoxNode).element;
            if (textNodeElement !== backNodeElement && checkRectIntersection(textNodeRect, sortedRects[i])) {
                this.onOverlapDetected(textNodeRect, sortedRects[i], sortedRects);
                return true;
            }
        }
        return false;
    }

    private checkNeedFilter(textElementBoxNode: ITextElementBoxNode): boolean {
        const { textBox } = textElementBoxNode;
        if (textElementBoxNode.element.type === UIModelType.CHART) return true;
        // 不可见字符直接过滤掉
        if (!isStringVisible(textBox.text)) return true;
        if (textBox.textStyle.textBackground !== 'transparent') return true;
        const elementFill = textElementBoxNode.element.getBackgroundFill();
        // 有图形的背景做填充，暂时放过不检测。
        const fillType = elementFill?.getShownFillType();
        if (fillType !== FillType.NoFill && fillType !== undefined) return true;
        // 文字无填充且无边框，过滤掉
        // @ts-ignore
        const documentElementPath: Array<number> | undefined = textBox.textStyle?.getCustomizeProp?.('slatePath');
        if (documentElementPath) {
            const paragraphElement = textElementBoxNode.textDocumentElement.children[documentElementPath[0]];
            const textElementStyle = paragraphElement.children[documentElementPath[1]];
            if (textElementStyle.noFill && !textElementStyle.border) return true;
        }
        return false;
    }

    private getActualRectFromTextBox(textBox: ITextBox): Rectangle {
        const { rect, glyphs } = textBox;
        let actualRect = new Rectangle({
            x: 0,
            y: 0,
            width: 0,
            height: 0,
        });
        let infoEnough = true;
        if (glyphs) {
            for (let i = 0; i < glyphs.length; i++) {
                const currentTextMetrics = glyphs[i].textMetrics;
                if (currentTextMetrics?.actualBoundingBoxAscent === undefined) {
                    infoEnough = false;
                    break;
                }
                // 排版层为了使排版后字间距和 office ppt 一致，处理竖排和堆积的时候并不是单纯调换文字的宽高，而是进行了重新计算，导致文字的实际高度 !== fontAscent + fontDescent。
                // 这种场景下如果使用 fontAscent 来计算文字实际位置的偏移量会出错，所以改为排版层重新计算过的 glyph[i].ascent。
                const {
                    actualBoundingBoxAscent,
                    actualBoundingBoxDescent,
                    actualBoundingBoxRight,
                    actualBoundingBoxLeft,
                } = currentTextMetrics;
                const currentY = twipsToPx(glyphs?.[i].ascent) - actualBoundingBoxAscent;
                const currentHeight = actualBoundingBoxAscent + actualBoundingBoxDescent;
                if (i === glyphs.length - 1) {
                    actualRect.width += actualBoundingBoxRight;
                } else {
                    actualRect.width += currentTextMetrics.width;
                }
                if (i === 0) {
                    actualRect.y = currentY;
                    actualRect.height = currentHeight;
                    actualRect.x = -actualBoundingBoxLeft;
                    actualRect.width -= actualRect.x;
                } else {
                    actualRect.y = Math.min(actualRect.y, currentY);
                    actualRect.height = Math.max(actualRect.height, currentHeight);
                }
            }
        }

        // @ts-ignore
        const rotateNum = textBox.rotate;
        if (rotateNum !== undefined) {
            actualRect = this.getRectAfterRotate(actualRect, rotateNum, rect);
        }
        if (!infoEnough) {
            if (rotateNum === 90 || rotateNum === 270) {
                actualRect.x = twipsToPx(rect.width * 0.1);
                actualRect.width = twipsToPx(rect.width * 0.8);
                actualRect.height = twipsToPx(rect.height);
            } else {
                actualRect.y = twipsToPx(rect.height * 0.1);
                actualRect.width = twipsToPx(rect.width);
                actualRect.height = twipsToPx(rect.height * 0.8);
            }
        }
        return actualRect;
    }

    private getRectAfterRotate(rect: Rectangle, rotate: number, wrapperRect: IRect): Rectangle {
        if (rotate === 90) {
            return new Rectangle({
                x: twipsToPx(wrapperRect.width) - rect.y - rect.height,
                y: rect.x,
                width: rect.height,
                height: rect.width,
            });
        }
        if (rotate === 270) {
            return new Rectangle({
                x: rect.y,
                y: twipsToPx(wrapperRect.height) - rect.x - rect.width,
                width: rect.height,
                height: rect.width,
            });
        }
        return rect;
    }

    private checkToDeleteTextRects(elementRect: Rectangle<any>, detectOverlap = false) {
        const overTextBoxArray = this.textQuadtree?.retrieve(elementRect);
        overTextBoxArray?.forEach((textBox) => {
            if (checkRectIntersection(elementRect, textBox)) {
                if (detectOverlap) {
                    this.onOverlapDetected(elementRect, textBox, overTextBoxArray);
                }
                this.textQuadtree?.remove(textBox);
            }
        });
    }
}
